import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import difference from 'lodash/difference';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import rfdc from 'rfdc';

import sightXLogo from '../../assets/sightXLogo.svg';
import RecipientVariablesModal from './recipientVariablesModal';
import ErrorReporter from './errorReporter';
import SubmitResponsesButton from '../common/submitResponsesButton';
import SurveyListener from '../common/surveyListener';
import Survey from './survey';
import VariablesViewer from './variablesViewer';

import { ITEM_TYPES } from '../types/objectTypes';
import { checkIsPrevButtonEnabled } from '../utils/utils';
import { getPageItems, validateItems } from './utils';

// TODO: check if audience variables should be calculated here or be given by the backend
import { getProjectAudienceVariables } from '../../utils/questionViewer.utils';

import {
  fetchProjectForSurvey,
  fetchQuestionForPreview,
  fetchProjectForSurveyUsingId,
  fetchSavedTranslation,
  fetchSurveyNextPage,
  surveyPrevPage,
} from '../../store/actions/activeProject/indexOld';
import { onChangeItem, submitResponse, removeErrors } from '../../store/actions/responses';
import { showNotification } from '../../store/actions/notifications';
import { setRecipientVariablesForPreview } from '../../store/actions/activeProject/survey';
import PreQuestionSubmitFactory from '../fields/common/preQuestionSubmit/preQuestionSubmitFactory';
import { hasQuestionPerRow } from '../utils/helpers';
import { resetSurvey } from '../../store/actions/globalActions';

import './capture.scss';

const cloneDeep = rfdc();

class Capture extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fetched: false,
      initialTime: null,
      recipientVariables: [],
      isRecipientVariablesModalOpen: false,
      isTest: false,
      wasNextPressed: false,
      lastPageQuestionsViewed: [],
    };
    this.submitResponse = this.submitResponse.bind(this);
    this.prevPage = this.prevPage.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.onSaveRecipientVariables = this.onSaveRecipientVariables.bind(this);
    this.onCancelSaveRecipientVariables = this.onCancelSaveRecipientVariables.bind(this);
    this.onShowPage = this.onShowPage.bind(this);
    this.fetchSurvey = this.fetchSurvey.bind(this);
  }

  onShowPage(event) {
    if (event.persisted) {
      this.props.resetSurvey();
      this.setState({
        fetched: false,
        initialTime: null,
        recipientVariables: [],
        isRecipientVariablesModalOpen: false,
        isTest: false,
        wasNextPressed: false,
        lastPageQuestionsViewed: [],
      });
      this.fetchSurvey();
    }
  }

  fetchSurvey() {
    const {
      match: { params = {} },
    } = this.props;
    const { fetched } = this.state;
    this.setState({
      initialTime: new Date(),
    });

    if (!fetched) {
      const queryParams = new URLSearchParams(window.location.search);
      const isTest = queryParams.has('test') || queryParams.has('isTest');
      this.setState({
        isTest,
      });

      if (params.hash) {
        this.props.fetchProjectForSurvey(params.hash, isTest);
      } else if (params.projectId && params.questionFrontendId) {
        this.props.fetchQuestionForPreview({
          projectId: params.projectId,
          frontendId: params.questionFrontendId,
        });
      } else {
        this.props.fetchProjectForSurveyUsingId(params.projectId);
      }
      this.setState({
        fetched: true,
      });
    }
  }

  componentDidMount() {
    this.fetchSurvey();
    window.addEventListener('pageshow', this.onShowPage);
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.onShowPage);
  }

  componentDidUpdate(prevProps, prevState) {
    const { fetched, isTest } = this.state;

    if (fetched !== prevState.fetched) {
      if (!fetched) {
        this.props.fetchProjectForSurvey(this.props.match.params.hash, isTest);
        this.setState({ fetched: true });
      }
    }

    const { selectedLanguage } = this.props;

    if (selectedLanguage !== prevProps.selectedLanguage) {
      const { isLanguageFetched, fetchingTranslations, projectId } = this.props;

      if (fetched && projectId && !isLanguageFetched && !fetchingTranslations) {
        this.props.fetchSavedTranslation(projectId, selectedLanguage);
      }
    }

    const { audience, criteria } = this.props;

    if (!isEqual(audience, prevProps.audience) || !isEqual(criteria, prevProps.criteria)) {
      const recipientVariables = getProjectAudienceVariables({ audience, criteria });
      this.setState({
        recipientVariables,
        isRecipientVariablesModalOpen: recipientVariables.length > 0,
      });
    }
  }

  onSaveRecipientVariables(recipientVariables = {}) {
    this.props.setRecipientVariablesForPreview(recipientVariables);
    this.setState({ isRecipientVariablesModalOpen: false });
  }

  onCancelSaveRecipientVariables() {
    this.setState({ isRecipientVariablesModalOpen: false });
  }

  scrollToTop() {
    window.scrollTo(0, 0);
    const capture = document.getElementsByTagName('body')[0];
    if (capture) {
      capture.scrollTop = 0;
    }
  }

  prevPage() {
    const { isPrevPageEnabled } = this.props;
    if (isPrevPageEnabled) {
      this.scrollToTop();
      let lastPageQuestionsViewed = [...this.state.lastPageQuestionsViewed];
      this.setState({
        lastPageQuestionsViewed: lastPageQuestionsViewed.slice(0, lastPageQuestionsViewed.length - 1),
      });
      this.props.surveyPrevPage();
    }
  }

  async nextPage() {
    await this.props.removeErrors();
    const {
      itemsToValidate,
      responses,
      uiTags,
      options,
      rows,
      surveyVariables,
      match: { params = {} },
    } = this.props;

    itemsToValidate.forEach((item) => {
      const answer = responses[item.frontendId] || {};
      const payload = {
        item,
        answer: cloneDeep(answer),
        rows,
        options,
      };
      const preSubmit = PreQuestionSubmitFactory.getStrategy(payload);
      preSubmit.handle();
    });

    const isValid = validateItems({
      itemsToValidate,
      responses,
      uiTags,
      options,
      rows,
      surveyVariables,
    });

    if (!isValid) {
      const { uiTags: { answersErrorMessage = '' } = {} } = this.props;
      this.props.showNotification({
        message: answersErrorMessage,
        level: 'error',
      });
      return;
    }
    const lastPageQuestionsViewed = [
      ...this.state.lastPageQuestionsViewed,
      this.state.lastPageQuestionsViewed.length === 0
        ? Object.keys(responses)
        : difference(
            Object.keys(responses),
            this.state.lastPageQuestionsViewed[this.state.lastPageQuestionsViewed.length - 1]
          ),
    ];
    this.setState({
      lastPageQuestionsViewed,
    });
    const questionsViewed = lastPageQuestionsViewed[lastPageQuestionsViewed.length - 1];
    this.props.fetchSurveyNextPage(questionsViewed);
    this.scrollToTop();
  }

  submitResponse(responses, recaptchaToken) {
    const { match: { params: { hash } = {} } = {}, history } = this.props;
    const { location } = history;
    const { search } = location;
    let isAdmin;

    if (search) {
      const queryParams = new URLSearchParams(search);
      isAdmin = queryParams.get('admin') === 'true';
    }
    const data = {
      answers: responses,
      createdAt: moment().format('YYYY-MM-DD HH:mm:ss'),
      hash,
      isAdmin,
    };
    const lastPageQuestionsViewed = [
      ...this.state.lastPageQuestionsViewed,
      this.state.lastPageQuestionsViewed.length === 0
        ? Object.keys(responses)
        : difference(
            Object.keys(responses),
            this.state.lastPageQuestionsViewed[this.state.lastPageQuestionsViewed.length - 1]
          ),
    ];
    const questionsViewed = lastPageQuestionsViewed[lastPageQuestionsViewed.length - 1];
    this.props.submitResponse(data, questionsViewed, recaptchaToken);
    this.scrollToTop();
  }

  renderErrorPage() {
    return (
      <div className="capture-error-page">
        <div className="error-page-title">Powered by</div>
        <img src={sightXLogo} alt="logo" />
        <div className="error-page-body">
          Thank you for your interest in taking part in our survey,
          <br />
          however this survey has now closed
        </div>
        <div className="error-page-footer">Is this survey yours?</div>
        <div className="error-page-actions">
          <a href="/">Log in</a>
        </div>
      </div>
    );
  }

  renderRecipientVariablesModal() {
    const { recipientVariables } = this.state;
    return (
      <RecipientVariablesModal
        recipientVariables={recipientVariables}
        onSave={this.onSaveRecipientVariables}
        onCancel={this.onCancelSaveRecipientVariables}
      />
    );
  }

  renderVariablesViewer() {
    if (!this.props.isPreview) {
      return null;
    }
    return <VariablesViewer />;
  }

  renderContent() {
    if (this.props.isSurveyAborted) {
      const { errorData: { error, message, data } = {}, match: { params: { hash } = {} } = {} } = this.props;
      const errorInfo = { message, data, hash };

      return <ErrorReporter error={error} errorInfo={errorInfo} />;
    }

    const {
      logo,
      onChangeItem,
      responses,
      isPrevPageEnabled,
      isNextPageEnabled,
      showNotification,
      isPreview,
    } = this.props;

    const { isTest } = this.state;

    if (this.state.isRecipientVariablesModalOpen && (isPreview || isTest)) {
      return this.renderRecipientVariablesModal();
    }

    return (
      <div className="capture">
        <Survey
          logo={logo}
          onChangeItem={onChangeItem}
          responses={responses}
          prevPage={this.prevPage}
          nextPage={this.nextPage}
          isPrevPageEnabled={isPrevPageEnabled}
          isNextPageEnabled={isNextPageEnabled}
          showNotification={showNotification}
          submitResponse={this.submitResponse}
          isPreview={isPreview}
        />
        <SubmitResponsesButton
          className="invisible"
          ref={(responsesButton) => {
            if (responsesButton) {
              this.responsesButton = responsesButton;
            }
          }}
          onSubmitResponses={this.submitResponse}
          handleClose={this.handleClose}
        />
      </div>
    );
  }

  render() {
    const { projectHasErrors = false } = this.props;
    return (
      <div>
        <SurveyListener />
        {this.renderVariablesViewer()}
        {projectHasErrors ? this.renderErrorPage() : this.renderContent()}
      </div>
    );
  }
}

const getAllowAnswers = (survey, items, rows) => {
  const allowAnswers = new Set();
  const { pages = [], breadcrumbs = [], currentPage } = survey;
  breadcrumbs.forEach((pageIndex) => {
    if (!pages[pageIndex - 1]) {
      return;
    }
    pages[pageIndex - 1].items.forEach((itemFrontendIdExpanded) => {
      const [itemFrontendId] = itemFrontendIdExpanded.toString().split('::');
      if (!items[itemFrontendId]) {
        return;
      }
      allowAnswers.add(items[itemFrontendId]._id);
      const currentItem = items[itemFrontendId];
      if (hasQuestionPerRow(currentItem) && currentItem.rows) {
        currentItem.rows.forEach((row) => {
          allowAnswers.add(rows[row].questionId);
        });
      }
      if (currentItem.type === ITEM_TYPES.contactInfo && currentItem.config) {
        if (currentItem.config.countryMultipleChoiceId && items[currentItem.config.countryMultipleChoiceId]) {
          allowAnswers.add(items[currentItem.config.countryMultipleChoiceId]._id);
        }
        if (currentItem.config.stateMultipleChoiceId && items[currentItem.config.stateMultipleChoiceId]) {
          allowAnswers.add(items[currentItem.config.stateMultipleChoiceId]._id);
        }
      }
    });
  });
  if (pages[currentPage - 1]) {
    pages[currentPage - 1].items.forEach((itemFrontendIdExpanded) => {
      const [itemFrontendId] = itemFrontendIdExpanded.toString().split('::');
      if (items[itemFrontendId]) {
        allowAnswers.add(items[itemFrontendId]._id);
        const currentItem = items[itemFrontendId];
        if (
          currentItem.type === ITEM_TYPES.input &&
          currentItem.config &&
          currentItem.config.multipleInputs &&
          currentItem.rows
        ) {
          currentItem.rows.forEach((row) => {
            allowAnswers.add(rows[row].questionId);
          });
        }
        if (currentItem.type === ITEM_TYPES.contactInfo && currentItem.config) {
          if (currentItem.config.countryMultipleChoiceId && items[currentItem.config.countryMultipleChoiceId]) {
            allowAnswers.add(items[currentItem.config.countryMultipleChoiceId]._id);
          }
          if (currentItem.config.stateMultipleChoiceId && items[currentItem.config.stateMultipleChoiceId]) {
            allowAnswers.add(items[currentItem.config.stateMultipleChoiceId]._id);
          }
        }
      }
    });
  }
  return Array.from(allowAnswers.values());
};

const mapStateToProps = (state) => {
  const {
    activeProject: {
      audience = {},
      criteria = {},
      project = {}, // TODO: Pending removal
      items = {},
      options = {},
      rows = {},
      survey: { currentPage, isSurveyAborted, errorData, projectId, totalPages, pageToShow } = {},
      survey = {},
      translations: { data: translations = {}, fetching: fetchingTranslations } = {},
      pages = [],
    } = {},
    customization: { logo },
    capture: { selectedLanguage, hasError: campaignHasErrors } = {},
    responses = {},
    surveyVariables = {},
  } = state;

  // TODO: Check isNextPageEnabled change, also checkIsPrevButtonEnabled will not have isSkipLogicEnabled
  const { hasErrors, isSkipLogicEnabled } = project;

  const page = pages[pageToShow] || {};
  const isPrevPageEnabled = checkIsPrevButtonEnabled(survey, pages);
  const isNextPageEnabled = !page.isLastPage;
  const translation = translations[selectedLanguage];
  let isLanguageFetched = translation && Object.keys(translation.items || {}).length > 0;
  const { uiTags = {} } = translation || {};
  const allowAnswers = getAllowAnswers(survey, items, rows);
  const itemsToValidate = getPageItems(pageToShow, pages, items);

  return {
    project: {}, // TODO: Remove project from the component
    projectId,
    logo,
    currentPage,
    isPrevPageEnabled,
    isNextPageEnabled,
    selectedLanguage,
    isLanguageFetched,
    fetchingTranslations,
    uiTags,
    allowAnswers,
    projectHasErrors: hasErrors || campaignHasErrors,
    audience,
    criteria,
    isSurveyAborted,
    errorData,
    surveyVariables,
    itemsToValidate,
    responses,
    options,
    rows,
  };
};

Capture.propTypes = {
  isPreview: PropTypes.bool,
};

export const ConnectedCapture = connect(mapStateToProps, {
  onChangeItem,
  fetchProjectForSurvey,
  fetchProjectForSurveyUsingId,
  fetchQuestionForPreview,
  fetchSurveyNextPage,
  surveyPrevPage,
  submitResponse,
  showNotification,
  fetchSavedTranslation,
  setRecipientVariablesForPreview,
  removeErrors,
  resetSurvey,
})(Capture);

export default withRouter(ConnectedCapture);
